// "Match to a customer" for a phone enquiry on the Enquiries page (Session ARM, D88): the office has
// worked out who an unknown caller actually is, and links the logged call to that customer.
//
// IT SENDS THE CUSTOMER AND NOTHING ELSE. LogCallModal's edit mode would have been the obvious thing
// to reuse, and it is exactly the wrong one: on save it rewrites the caller's name and phone from its
// own form, which for a matched call it empties, so the name and number the office typed while the
// caller was on the line would be wiped the moment the call was matched. PATCH /api/call-logs/:id
// with `{ customerId }` alone changes the link and leaves every other field as it was.
//
// Once matched, the call leaves the Enquiries page (it is no longer an unknown caller) and lives on
// that customer's Call log tab, which is where the host sends the office next.
import { useState } from "react";
import { RecordPicker } from "../../components/RecordPicker";
import { api } from "../../lib/api";
import type { CallLogDto, CustomerOption } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { callerLine } from "./AcceptCallAsWorkModal";

const FIELD = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";

export function MatchCallModal({
  call,
  onClose,
  onMatched,
}: {
  call: Pick<CallLogDto, "id" | "customerId" | "customerName" | "callerName" | "callerPhone" | "topic">;
  onClose: () => void;
  onMatched: (customer: CustomerOption) => void;
}) {
  const [chosen, setChosen] = useState<CustomerOption | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!chosen) return;
    setSaving(true);
    setError(null);
    try {
      // The customer and nothing else: see the header.
      await api.callLogs.update(call.id, { customerId: chosen.id });
      onMatched(chosen);
    } catch (err) {
      setError(friendlyApiError(err, "Could not match the call. Please try again."));
      setSaving(false);
    }
  }

  return (
    <ModalShell ariaLabel="Match this call to a customer" onClose={() => !saving && onClose()} closeOnBackdrop width="max-w-lg" panelClassName="p-5">
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Match this call to a customer</h2>
      <p className="mb-3 text-sm text-ele-body">
        Links the call to the customer it was from. It then leaves the Enquiries page and appears on that customer&apos;s Call log tab. The
        caller&apos;s name, number and what they rang about are kept exactly as logged.
      </p>

      <div className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm">
        <p className="text-ele-strong">{callerLine(call)}</p>
        {call.topic && <p className="mt-1 text-ele-body">{call.topic}</p>}
      </div>

      {chosen ? (
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className={`${FIELD} flex-1`}>{chosen.name}</div>
          <button type="button" onClick={() => setChosen(null)} disabled={saving} className="font-menu text-xs tracking-wide text-ele-body">
            Change
          </button>
        </div>
      ) : (
        <div className="mb-3">
          <RecordPicker
            kind="customer"
            value=""
            label="Search customers by name"
            placeholder="Search customers by name"
            onChange={(id, rec) => {
              if (id && rec) setChosen(rec.raw as unknown as CustomerOption);
            }}
          />
        </div>
      )}

      {error && (
        <p role="alert" className="mb-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      <div className={`mt-1 gap-2 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!chosen || saving}
          className={`rounded-ele bg-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-bg disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          {saving ? "Matching..." : chosen ? `Match to ${chosen.name}` : "Match"}
        </button>
      </div>
    </ModalShell>
  );
}
